import { Box } from '@mui/material';
import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import ImageSettings, { Size } from '../models/BlockComponentSettings/ImageSettings';
import BlockComponentImage from '../models/BlockComponentValues/BlockComponentImage';
import { fitParent } from '../utils/image';
import { useGetBase64Image } from '../hooks/UseGetBase64Image';

interface CroppedImageProps {
  image: BlockComponentImage;
  settings?: ImageSettings;
  sizeInPercentage: { width: string, height: string };
}

const CroppedImage = ({
  image, sizeInPercentage,
}: CroppedImageProps) => {
  const [size, setSize] = useState({ width: 0, height: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const parent: Size = { width: containerRef.current?.clientWidth, height: containerRef.current?.clientHeight };
    const child: Size = image.settings.frameSize;
    setSize(fitParent(parent, child));
  }, [image.settings.frameSize]);

  const percentPosition = useMemo(() => {
    const { x, y } = image.settings.position;
    const xPercent = (x / image.settings.size.width) * 100;
    const yPercent = (y / image.settings.size.height) * 100;
    return { x: xPercent, y: yPercent };
  }, [image.settings]);

  const base64Image = useGetBase64Image(image.value);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: sizeInPercentage.width,
        height: sizeInPercentage.height,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          width: size.width,
          height: size.height,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <img
          alt=""
          src={base64Image}
          style={{
            position: 'absolute',
            objectFit: 'contain',
            top: '50%',
            left: '50%',
            transform: `translate(-50%, -50%) translate(${percentPosition.x}%, ${percentPosition.y}%) scale(${image.settings.scale})`,
            width: image.settings.size.width / (image.settings.frameSize.width / size.width),
            height: image.settings.size.height / (image.settings.frameSize.height / size.height),
          }}
        />
      </Box>
    </Box>
  );
};

export default CroppedImage;
